<div class="camera-container">
  <!-- Error Message -->
  @if (error) {
    <div class="error-message">
      <mat-icon>error</mat-icon>
      <p>{{ error }}</p>
      <button class="button primary" (click)="startCamera()">{{ 'camera-photo.buttons.retry-camera' | translate }}</button>
    </div>
  }

  <!-- Initialization Message -->
  @if (isInitializing && !error) {
    <div class="initialization-message">
      <mat-spinner></mat-spinner>
      <p>{{ 'camera-photo.messages.initializing-camera' | translate }}</p>
      <p class="hint">{{ 'camera-photo.messages.please-wait-setup' | translate }}</p>
    </div>
  }

  <!-- Camera Section -->
  @if (!error) {
    <div class="camera-section">
      <div class="video-container">
        <video #video autoplay playsinline muted [style.display]="isVideoReady ? 'block' : 'none'"></video>
        <canvas #canvas style="display: none;"></canvas>
        <!-- Shutter flash, so an instant capture is visible -->
        @if (isFlashing) {
          <div class="capture-flash"></div>
        }
        <!-- Loading overlay -->
        @if ((!isVideoReady && !isInitializing) || isStartingCamera) {
          <div class="loading-overlay">
            <mat-spinner></mat-spinner>
            <p>{{ 'camera-photo.messages.starting-camera' | translate }}</p>
          </div>
        }
      </div>
      <!-- Camera Controls -->
      <div class="camera-controls">
        <button class="button capture-button primary"
          (click)="capturePhoto()"
          [disabled]="!isVideoReady">
          <mat-icon>camera_alt</mat-icon>
          @if (capturedPhotos.length > 0) {
            <span class="capture-count" aria-live="polite">
              {{ capturedPhotos.length }}
            </span>
          }
        </button>
        <!-- Most recent shot: confirms the capture landed and jumps to the full list, which is
        otherwise below the fold on a phone -->
        @if (lastPhoto) {
          <button class="last-photo-thumb"
            (click)="scrollToPhotos()"
            [attr.aria-label]="'camera-photo.buttons.view-photos' | translate"
            [title]="'camera-photo.buttons.view-photos' | translate">
            <img [src]="lastPhoto.dataUrl" alt="">
          </button>
        }
        @if (hasMultipleCameras) {
          <button class="button secondary"
            (click)="switchCamera()"
            [disabled]="!isVideoReady"
            >
            <mat-icon>flip_camera_android</mat-icon>
            <span class="button-text">{{ 'camera-photo.buttons.switch-camera' | translate }}</span>
          </button>
        }
        @if (isCameraActive) {
          <button class="button secondary"
            (click)="stopCamera()"
            [disabled]="!isCameraActive"
            >
            <mat-icon>stop</mat-icon>
            <span class="button-text">{{ 'camera-photo.buttons.stop-camera' | translate }}</span>
          </button>
        }
        @if (!isCameraActive && !error && !isInitializing) {
          <button class="button primary"
            (click)="startCamera()"
            [disabled]="isStartingCamera"
            >
            <mat-icon>videocam</mat-icon>
            <span class="button-text">{{ isStartingCamera ? ('camera-photo.buttons.starting' | translate) : ('camera-photo.buttons.start-camera' | translate) }}</span>
          </button>
        }
      </div>
    </div>
  }

  <!-- Photos Section -->
  @if (capturedPhotos.length > 0) {
    <div class="photos-section" #photosSection>
      <div class="photos-header">
        <h3>
          {{ 'camera-photo.labels.captured-photos' | translate }} ({{ capturedPhotos.length }})
          <span class="estimated-size">
            {{ 'camera-photo.labels.estimated-size' | translate: { size: estimatedPdfSizeLabel } }}
          </span>
        </h3>
        <div class="header-controls">
          <button class="button warning" (click)="clearAllPhotos()">
            <mat-icon>delete_sweep</mat-icon>
            <span class="button-text">{{ 'camera-photo.buttons.clear-all' | translate }}</span>
          </button>
        </div>
      </div>
      @if (isOverSizeBudget) {
        <div class="size-warning">
          <mat-icon>error</mat-icon>
          <p>{{ 'camera-photo.errors.size-limit-exceeded' | translate: { size: estimatedPdfSizeLabel, max: maxSizeLabel } }}</p>
        </div>
      }
      <div class="photos-grid">
        @for (photo of capturedPhotos; track trackByPhotoId($index, photo)) {
          <div class="photo-item">
            <img [src]="photo.dataUrl" [alt]="('camera-photo.labels.photo-alt' | translate) + photo.timestamp">
            <div class="photo-overlay">
              <button class="button icon-only warning" (click)="deletePhoto(photo.id)">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
            <div class="photo-timestamp">
              {{ photo.timestamp | date:'short' }}
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Empty State -->
  @if (capturedPhotos.length === 0 && !error) {
    <div class="empty-state">
      <mat-icon>photo_camera</mat-icon>
      <p>{{ 'camera-photo.messages.no-photos-yet' | translate }}</p>
      <p class="hint">{{ 'camera-photo.messages.use-camera-hint' | translate }}</p>
    </div>
  }
</div>
